import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Alert, Badge, Button, ConfigProvider, Empty, Flex, Input, Pagination, Segmented, Select, Spin, Tag, Tooltip, Typography, message, theme } from "antd";
import {
  ArrowLeft,
  Camera,
  CameraOff,
  ChevronLeft,
  ChevronRight,
  Hand,
  History,
  Maximize,
  Megaphone,
  Mic,
  MicOff,
  Minimize,
  PhoneOff,
  Users,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useGetProctoringSession, useSetOpenAudio } from "@hooks";
import { CandidateTile, candidateName, examStateTag } from "./CandidateTile";
import { CandidateMediaControls } from "./CandidateMediaControls";
import { CandidateAudio, CandidateVideo } from "./CandidateVideo";
import { ProctorAssignmentsDrawer } from "./ProctorAssignmentsDrawer";
import { useProctoringRoom, type ParticipantMedia } from "./useProctoringRoom";
import { useStaffTalk } from "./useStaffTalk";

type Filter = "all" | "connected" | "disconnected" | "help";
type Scope = "mine" | "all";
type TalkTarget = { kind: "one"; identity: string } | { kind: "all" } | null;
type Tone = "info" | "success" | "warning" | "error";

interface ActivityEntry {
  id: number;
  at: Date;
  identity?: string;
  text: string;
  tone: Tone;
}

const PAGE_SIZES = [4, 9, 16, 25, 36, 49, 100];
const groupStyle: CSSProperties = { display: "flex", flexDirection: "column", gap: 6, padding: "0 12px" };
const groupCaption: CSSProperties = { fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 };
const TONE_COLOR: Record<Tone, string> = { info: "#8c8c8c", success: "#52c41a", warning: "#fa8c16", error: "#ff4d4f" };

const beep = () => {
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") ctx.resume();
    [
      { at: 0, freq: 660 },
      { at: 0.18, freq: 880 },
      { at: 0.36, freq: 1100 },
    ].forEach(({ at, freq }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.25, ctx.currentTime + at);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + at + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + at + 0.25);
    });
  } catch {
    // Audio may still be blocked; the banner, pulsing tile and tab title show the request anyway.
  }
};

// Browsers only allow sound after a user gesture, so one shared context is unlocked on the first click in the wall.
let sharedAudioContext: AudioContext | null = null;
const getAudioContext = () => (sharedAudioContext ??= new AudioContext());
const unlockAudio = () => {
  getAudioContext().resume().catch(() => undefined);
};
const HELP_REMINDER_MS = 30000;

interface Props {
  termId: string;
  /** Rendered inside the term monitoring page instead of its own page. */
  embedded?: boolean;
  onBack?: () => void;
}

/// Live proctoring wall shared by the term monitoring page (admins) and the standalone page (assessors, full screen).
export const ProctoringWall = ({ termId, embedded = false, onBack }: Props) => {
  const { t } = useTranslation();
  const wallRef = useRef<HTMLDivElement>(null);

  const { data: session, isLoading, isError } = useGetProctoringSession(termId);
  const { media, status, error, room, openAudio } = useProctoringRoom(session?.serverUrl, session?.token);
  const setOpenAudioMode = useSetOpenAudio(termId);

  const [scope, setScope] = useState<Scope | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [listening, setListening] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [talkTo, setTalkTo] = useState<TalkTarget>(null);
  const [talkCamera, setTalkCamera] = useState(false);
  const [talkMic, setTalkMic] = useState(true);
  const [showActivity, setShowActivity] = useState(true);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);

  const allCandidates = useMemo(() => session?.candidates ?? [], [session]);
  const myCount = allCandidates.filter((c) => c.assessorId === session?.currentUserId).length;
  // Admins who are also assessors see their own candidates first; they can still switch to everyone.
  const canChooseScope = !!session?.canManageAssignments && !!session?.isAssessor && myCount > 0;
  const effectiveScope: Scope = scope ?? (canChooseScope ? "mine" : "all");
  const candidates = useMemo(
    () => (effectiveScope === "mine" ? allCandidates.filter((c) => c.assessorId === session?.currentUserId) : allCandidates),
    [allCandidates, effectiveScope, session?.currentUserId]
  );
  const nameOf = useMemo(() => {
    const map: Record<string, string> = {};
    allCandidates.forEach((c) => (map[c.identity] = candidateName(c)));
    return map;
  }, [allCandidates]);

  const log = useCallback((text: string, tone: Tone, identity?: string) => {
    setActivity((list) => [{ id: Date.now() + Math.random(), at: new Date(), identity, text, tone }, ...list].slice(0, 300));
  }, []);

  // Turn room state changes into a readable activity log (who asked for help, who switched devices off, who left).
  const previous = useRef<Record<string, ParticipantMedia> | null>(null);
  useEffect(() => {
    if (status !== "connected") return;
    const before = previous.current;
    previous.current = media;
    if (!before) return;
    let newHelp = false;
    for (const c of candidates) {
      const was = before[c.identity];
      const now = media[c.identity];
      const name = nameOf[c.identity];
      if (!!was?.connected !== !!now?.connected) {
        log(now?.connected ? t("proctoring.evConnected", { name }) : t("proctoring.evDisconnected", { name }), now?.connected ? "info" : "warning", c.identity);
        continue;
      }
      if (!now?.connected || !was) continue;
      if (!was.help && now.help) {
        newHelp = true;
        log(t("proctoring.evHelp", { name }), "warning", c.identity);
      }
      if (was.help && !now.help) log(t("proctoring.evHelpCleared", { name }), "info", c.identity);
      if (was.cameraOn && now.cameraOn === false) log(t("proctoring.evCameraOff", { name }), "error", c.identity);
      if (was.cameraOn === false && now.cameraOn) log(t("proctoring.evCameraOn", { name }), "success", c.identity);
      if (was.micOn && now.micOn === false) log(t("proctoring.evMicOff", { name }), "warning", c.identity);
      if (was.micOn === false && now.micOn) log(t("proctoring.evMicOn", { name }), "success", c.identity);
    }
    if (newHelp) beep();
  }, [media, status, candidates, nameOf, log, t]);

  const connectedCount = candidates.filter((c) => media[c.identity]?.connected).length;
  const helpList = candidates.filter((c) => media[c.identity]?.connected && media[c.identity]?.help);
  const receivedVideos = Object.values(media).filter((m) => m.video).length;

  useEffect(() => {
    if (helpList.length === 0) return;
    const original = document.title;
    document.title = `(${helpList.length}) ✋ ${t("proctoring.helpRequested")}`;
    return () => {
      document.title = original;
    };
  }, [helpList.length, t]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates.filter((c) => {
      const m = media[c.identity];
      if (filter === "connected" && !m?.connected) return false;
      if (filter === "disconnected" && m?.connected) return false;
      if (filter === "help" && !(m?.connected && m?.help)) return false;
      return !q || `${candidateName(c)} ${c.email ?? ""}`.toLowerCase().includes(q);
    });
  }, [candidates, media, search, filter]);

  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageItems = visible.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const columns = Math.max(1, Math.ceil(Math.sqrt(pageItems.length)));
  const rows = Math.max(1, Math.ceil(pageItems.length / columns));
  const compact = columns >= 6;

  const focusedIndex = visible.findIndex((c) => c.identity === focusedId);
  const focused = focusedIndex >= 0 ? visible[focusedIndex] : candidates.find((c) => c.identity === focusedId);
  const focusedMedia = focused ? media[focused.identity] : undefined;

  const talkTargets = useMemo(() => {
    if (talkTo?.kind === "one") return media[talkTo.identity]?.connected ? [talkTo.identity] : [];
    if (talkTo?.kind === "all") return candidates.filter((c) => media[c.identity]?.connected).map((c) => c.identity);
    return [];
  }, [talkTo, candidates, media]);
  useStaffTalk(room, talkTargets, talkMic, talkCamera);

  const startTalk = (identity: string) => {
    setTalkTo({ kind: "one", identity });
    setTalkMic(true);
    log(t("proctoring.evTalkStart", { name: nameOf[identity] }), "success", identity);
  };
  const stopTalk = () => {
    if (talkTo?.kind === "one") log(t("proctoring.evTalkStop", { name: nameOf[talkTo.identity] }), "info", talkTo.identity);
    if (talkTo?.kind === "all") log(t("proctoring.evAnnounceStop"), "info");
    setTalkTo(null);
    setTalkCamera(false);
    setTalkMic(true);
  };
  // The proctor's camera is opt-in: it can be shown to the candidate with or without speaking.
  const toggleMyCamera = (identity?: string) => {
    if (!talkTo && identity) {
      setTalkTo({ kind: "one", identity });
      setTalkMic(false);
      setTalkCamera(true);
      log(t("proctoring.evCameraShown", { name: nameOf[identity] }), "success", identity);
      return;
    }
    if (talkCamera && !talkMic) return stopTalk();
    setTalkCamera((v) => !v);
  };
  const toggleMyMic = () => {
    if (talkMic && !talkCamera) return stopTalk();
    setTalkMic((v) => !v);
  };
  const answerHelp = (identity: string) => {
    setFocusedId(identity);
    setListening(true);
    startTalk(identity);
  };

  // Open conversation: the proctor who starts it also speaks to everyone; all proctors hear all candidates.
  const toggleOpenAudio = () => {
    const enable = !openAudio;
    setOpenAudioMode.mutate(enable, {
      onSuccess: () => {
        log(enable ? t("proctoring.evOpenAudioOn") : t("proctoring.evOpenAudioOff"), enable ? "success" : "info");
        if (enable) {
          setTalkTo({ kind: "all" });
          setTalkMic(true);
        } else if (talkTo?.kind === "all") {
          setTalkTo(null);
          setTalkCamera(false);
        }
      },
      onError: () => message.error(t("proctoring.openAudioFailed")),
    });
  };

  const helpCountRef = useRef(0);
  helpCountRef.current = helpList.length;
  useEffect(() => {
    if (helpList.length === 0) return;
    const timer = window.setInterval(() => helpCountRef.current > 0 && beep(), HELP_REMINDER_MS);
    return () => window.clearInterval(timer);
  }, [helpList.length > 0]);

  const step = useCallback(
    (delta: number) => {
      if (visible.length === 0 || focusedIndex < 0) return;
      setFocusedId(visible[(focusedIndex + delta + visible.length) % visible.length].identity);
      setListening(false);
    },
    [visible, focusedIndex]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!focusedId || (e.target as HTMLElement)?.tagName === "INPUT") return;
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "Escape") setFocusedId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusedId, step]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === wallRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else wallRef.current?.requestFullscreen();
  };

  if (isLoading) return <Spin style={{ display: "block", marginTop: 80 }} />;
  if (isError || !session) return <Alert type="error" showIcon message={t("proctoring.connectionError")} />;

  const focusedName = focused ? candidateName(focused) : "";
  const talkingToFocused = talkTo?.kind === "one" && talkTo.identity === focused?.identity;

  return (
    <div
      ref={wallRef}
      data-testid="proctoring-wall"
      data-received-videos={receivedVideos}
      data-open-audio={openAudio}
      onPointerDown={unlockAudio}
      style={{
        display: "flex",
        flexDirection: "column",
        height: isFullscreen ? "100vh" : embedded ? "calc(100vh - 230px)" : "calc(100vh - 120px)",
        minHeight: 520,
        background: "#0b0b0b",
        padding: 8,
        gap: 8,
        borderRadius: isFullscreen ? 0 : 8,
      }}
    >
      <style>{`@keyframes proctorHelpPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(250,140,22,0); } 50% { box-shadow: 0 0 0 6px rgba(250,140,22,0.6); } }`}</style>

      <Flex gap={8} align="center" wrap="wrap">
        {onBack && (
          <Tooltip title={t("proctoring.back")}>
            <Button icon={<ArrowLeft size={16} />} onClick={onBack} />
          </Tooltip>
        )}
        {!embedded && (
          <Typography.Text strong style={{ color: "#fff", fontSize: 16 }}>
            {session.examTitle}
          </Typography.Text>
        )}
        <Tag color={status === "connected" ? "green" : status === "error" ? "red" : "default"} data-testid="proctoring-status">
          {t("proctoring.connected")}: {connectedCount}/{candidates.length}
        </Tag>
        {canChooseScope && (
          <Segmented<Scope>
            value={effectiveScope}
            onChange={(v) => {
              setScope(v);
              setPage(1);
            }}
            options={[
              { value: "mine", label: `${t("proctoring.scopeMine")} (${myCount})` },
              { value: "all", label: `${t("proctoring.scopeAll")} (${allCandidates.length})` },
            ]}
            data-testid="proctoring-scope"
          />
        )}
        <Input.Search
          allowClear
          placeholder={t("proctoring.search")}
          style={{ width: 200 }}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <Segmented<Filter>
          value={filter}
          onChange={(v) => {
            setFilter(v);
            setPage(1);
          }}
          options={[
            { value: "all", label: t("proctoring.filterAll") },
            { value: "connected", label: t("proctoring.filterConnected") },
            { value: "disconnected", label: t("proctoring.filterDisconnected") },
            {
              value: "help",
              label: (
                <span data-testid="filter-help" style={{ color: helpList.length > 0 ? "#fa8c16" : undefined }}>
                  <Hand size={12} /> {t("proctoring.filterHelp")} ({helpList.length})
                </span>
              ),
            },
          ]}
        />
        <Select
          value={pageSize}
          style={{ width: 130 }}
          onChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
          options={PAGE_SIZES.map((n) => ({ value: n, label: n === 100 ? t("proctoring.all") : `${n} ${t("proctoring.perPage")}` }))}
        />
        <Pagination simple current={currentPage} total={visible.length} pageSize={pageSize} onChange={setPage} />
        <Flex gap={8} style={{ marginLeft: "auto" }}>
          <Tooltip title={t("proctoring.openAudioHint")}>
            <Button
              type={openAudio ? "primary" : "default"}
              style={openAudio ? { background: "#30a46c" } : undefined}
              icon={<Users size={16} />}
              loading={setOpenAudioMode.isPending}
              disabled={!room || connectedCount === 0}
              onClick={toggleOpenAudio}
              data-testid="open-audio-toggle"
            >
              {openAudio ? t("proctoring.openAudioOff") : t("proctoring.openAudioOn")}
            </Button>
          </Tooltip>
          <Button
            icon={<Megaphone size={16} />}
            disabled={!room || connectedCount === 0 || talkTo?.kind === "all"}
            onClick={() => {
              setTalkTo({ kind: "all" });
              log(t("proctoring.evAnnounceStart", { count: connectedCount }), "success");
            }}
            data-testid="talk-all"
          >
            {t("proctoring.talkToAll")}
          </Button>
          {session.canManageAssignments && (
            <Button icon={<Users size={16} />} onClick={() => setAssignOpen(true)} data-testid="open-assignments">
              {t("proctoring.assignments")}
            </Button>
          )}
          <Tooltip title={t("proctoring.activity")}>
            <Badge dot={!showActivity && activity.length > 0}>
              <Button icon={<History size={16} />} type={showActivity ? "primary" : "default"} onClick={() => setShowActivity((v) => !v)} />
            </Badge>
          </Tooltip>
          <Button icon={isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />} onClick={toggleFullscreen}>
            {isFullscreen ? t("proctoring.exitFullscreen") : t("proctoring.fullscreen")}
          </Button>
        </Flex>
      </Flex>

      {error && <Alert type="error" showIcon message={`${t("proctoring.connectionError")}: ${error}`} />}

      {openAudio && (
        <Flex
          align="center"
          gap={8}
          wrap="wrap"
          data-testid="open-audio-banner"
          style={{ background: "#30a46c", color: "#fff", borderRadius: 6, padding: "6px 10px" }}
        >
          <Volume2 size={18} />
          <Typography.Text strong style={{ color: "#fff" }}>
            {t("proctoring.openAudioBanner")}
          </Typography.Text>
          <Button size="small" onClick={toggleOpenAudio} loading={setOpenAudioMode.isPending}>
            {t("proctoring.openAudioOff")}
          </Button>
        </Flex>
      )}
      {/* While the conversation is open, every proctor hears every candidate on this wall. */}
      {openAudio &&
        candidates.map((c) => {
          const audio = media[c.identity]?.audio;
          return audio && !(listening && focused?.identity === c.identity) ? <CandidateAudio key={c.identity} track={audio} /> : null;
        })}

      {helpList.length > 0 && (
        <Flex
          align="center"
          gap={8}
          wrap="wrap"
          data-testid="help-banner"
          style={{ background: "#fa8c16", color: "#000", borderRadius: 6, padding: "6px 10px" }}
        >
          <Hand size={18} />
          <Typography.Text strong>{t("proctoring.helpBanner", { count: helpList.length })}</Typography.Text>
          {helpList.map((c) => (
            <Button key={c.identity} size="small" icon={<Mic size={14} />} onClick={() => answerHelp(c.identity)} data-testid="help-answer">
              {t("proctoring.answer", { name: candidateName(c) })}
            </Button>
          ))}
        </Flex>
      )}

      {talkTo && (
        <Flex
          align="center"
          gap={8}
          wrap="wrap"
          data-testid="talk-banner"
          style={{ background: "#2251ff", color: "#fff", borderRadius: 6, padding: "6px 10px" }}
        >
          {talkTo.kind === "one" ? <Mic size={18} /> : <Megaphone size={18} />}
          <Typography.Text strong style={{ color: "#fff" }}>
            {talkTo.kind === "one"
              ? t("proctoring.talkingTo", { name: nameOf[talkTo.identity] })
              : t("proctoring.announcingTo", { count: talkTargets.length })}
          </Typography.Text>
          {talkTargets.length === 0 && <Tag color="red">{t("proctoring.notConnected")}</Tag>}
          <ConfigProvider theme={{ algorithm: theme.darkAlgorithm }}>
            <Button
              size="small"
              icon={talkMic ? <Mic size={14} /> : <MicOff size={14} />}
              onClick={toggleMyMic}
              data-testid="my-mic-toggle"
            >
              {talkMic ? t("proctoring.myMicOff") : t("proctoring.myMicOn")}
            </Button>
            <Button
              size="small"
              icon={talkCamera ? <CameraOff size={14} /> : <Camera size={14} />}
              onClick={() => toggleMyCamera()}
              data-testid="my-camera-toggle"
            >
              {talkCamera ? t("proctoring.myCameraOff") : t("proctoring.myCameraOn")}
            </Button>
          </ConfigProvider>
          <Button size="small" danger type="primary" icon={<PhoneOff size={14} />} onClick={stopTalk} data-testid="talk-stop">
            {t("proctoring.stopTalking")}
          </Button>
        </Flex>
      )}

      <Flex style={{ flex: 1, minHeight: 0 }} gap={8}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {focused ? (
            <Flex style={{ flex: 1, minHeight: 0 }} gap={8}>
              <Button type="text" icon={<ChevronLeft color="#fff" />} onClick={() => step(-1)} style={{ alignSelf: "center" }} />
              <Flex vertical gap={8} style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
              <div
                style={{ flex: 1, minHeight: 0, position: "relative", borderRadius: 8, overflow: "hidden", background: "#000" }}
                data-testid="proctor-focus"
              >
                {focusedMedia?.video && focusedMedia.cameraOn !== false ? (
                  <CandidateVideo track={focusedMedia.video} fit="contain" />
                ) : (
                  <Flex align="center" justify="center" style={{ height: "100%", color: "#8c8c8c" }}>
                    {!focusedMedia?.connected
                      ? t("proctoring.notConnected")
                      : focusedMedia.cameraOn === false
                        ? t("proctoring.cameraOff")
                        : t("proctoring.noVideo")}
                  </Flex>
                )}
                {listening && focusedMedia?.audio && <CandidateAudio track={focusedMedia.audio} />}
                <Flex
                  align="center"
                  gap={8}
                  wrap="wrap"
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 0,
                    padding: "10px 12px",
                    background: "linear-gradient(180deg, rgba(0,0,0,0.8) 0%, transparent 100%)",
                  }}
                >
                  <Typography.Text style={{ color: "#fff", fontSize: 20, whiteSpace: "nowrap" }} strong>
                    {focusedName}
                  </Typography.Text>
                  {examStateTag(focused.examInstanceState, t)}
                  {focused.assessorName && <Tag>{t("proctoring.assessor")}: {focused.assessorName}</Tag>}
                  {focusedMedia?.connected && focusedMedia.cameraOn === false && <Tag color="red">{t("proctoring.cameraOff")}</Tag>}
                  {focusedMedia?.connected && focusedMedia.micOn === false && <Tag color="red">{t("proctoring.micOff")}</Tag>}
                  {focusedMedia?.help && <Tag color="orange">{t("proctoring.helpRequested")}</Tag>}
                  {talkingToFocused && <Tag color="blue">{t("proctoring.youAreTalking")}</Tag>}
                </Flex>
              </div>
              <ConfigProvider theme={{ algorithm: theme.darkAlgorithm }}>
                <Flex
                  align="stretch"
                  gap={0}
                  wrap="wrap"
                  data-testid="focus-actions"
                  style={{ background: "#141414", borderRadius: 8, padding: "8px 4px" }}
                >
                  <div style={groupStyle}>
                    <Typography.Text type="secondary" style={groupCaption}>
                      {t("proctoring.groupConversation")}
                    </Typography.Text>
                    <Flex gap={8} wrap="wrap">
                      <Button
                        type="primary"
                        danger={talkingToFocused}
                        icon={talkingToFocused ? <PhoneOff size={16} /> : <Mic size={16} />}
                        disabled={!room || !focusedMedia?.connected || talkTo?.kind === "all"}
                        onClick={() => (talkingToFocused ? stopTalk() : startTalk(focused.identity))}
                        data-testid="talk-focused"
                      >
                        {talkingToFocused ? t("proctoring.stopTalking") : t("proctoring.talk")}
                      </Button>
                      <Tooltip title={t("proctoring.myCameraHint")}>
                        <Button
                          icon={talkingToFocused && talkCamera ? <CameraOff size={16} /> : <Camera size={16} />}
                          disabled={!room || !focusedMedia?.connected || talkTo?.kind === "all" || (!!talkTo && !talkingToFocused)}
                          onClick={() => toggleMyCamera(focused.identity)}
                          data-testid="focus-my-camera"
                        >
                          {talkingToFocused && talkCamera ? t("proctoring.myCameraOff") : t("proctoring.myCameraOn")}
                        </Button>
                      </Tooltip>
                      <Tooltip title={!focusedMedia?.audio ? t("proctoring.listenUnavailable") : undefined}>
                        <Button
                          icon={listening ? <Volume2 size={16} /> : <VolumeX size={16} />}
                          disabled={!focusedMedia?.audio}
                          onClick={() => setListening((v) => !v)}
                        >
                          {listening ? t("proctoring.stopListeningShort") : t("proctoring.listenShort")}
                        </Button>
                      </Tooltip>
                    </Flex>
                  </div>
                  <div style={{ ...groupStyle, borderLeft: "1px solid #303030" }}>
                    <Typography.Text type="secondary" style={groupCaption}>
                      {t("proctoring.groupDevices")}
                    </Typography.Text>
                    {focusedMedia?.connected ? (
                      <CandidateMediaControls
                        examTermId={termId}
                        examInstanceId={focused.examInstanceId}
                        candidateName={focusedName}
                        media={focusedMedia}
                        size="middle"
                        showLabels
                        onSent={(done) => log(`${t("proctoring.you")} → ${focusedName}: ${done}`, "info", focused.identity)}
                      />
                    ) : (
                      <Typography.Text type="secondary">{t("proctoring.notConnected")}</Typography.Text>
                    )}
                  </div>
                  <div style={{ ...groupStyle, marginLeft: "auto", alignItems: "flex-end" }}>
                    <Typography.Text type="secondary" style={groupCaption}>
                      {t("proctoring.focusHint")}
                    </Typography.Text>
                    <Button icon={<ArrowLeft size={16} />} onClick={() => setFocusedId(null)}>
                      {t("proctoring.backToGrid")}
                    </Button>
                  </div>
                </Flex>
              </ConfigProvider>
            </Flex>
            <Button type="text" icon={<ChevronRight color="#fff" />} onClick={() => step(1)} style={{ alignSelf: "center" }} />
            </Flex>
          ) : pageItems.length === 0 ? (
            <Empty description={<span style={{ color: "#8c8c8c" }}>{t("proctoring.noCandidates")}</span>} style={{ marginTop: 80 }} />
          ) : (
            <div
              data-testid="proctor-grid"
              style={{
                flex: 1,
                minHeight: 0,
                display: "grid",
                gap: 6,
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
              }}
            >
              {pageItems.map((c) => (
                <CandidateTile
                  key={c.identity}
                  examTermId={termId}
                  candidate={c}
                  media={media[c.identity]}
                  compact={compact}
                  showAssessor={session.canManageAssignments && effectiveScope === "all"}
                  talking={talkTargets.includes(c.identity)}
                  onClick={() => {
                    setFocusedId(c.identity);
                    setListening(false);
                  }}
                  onAction={(done) => log(`${t("proctoring.you")} → ${candidateName(c)}: ${done}`, "info", c.identity)}
                />
              ))}
            </div>
          )}
        </div>

        {showActivity && (
          <div
            data-testid="activity-panel"
            style={{ width: 290, flex: "none", background: "#141414", borderRadius: 8, padding: 8, overflowY: "auto", minHeight: 0 }}
          >
            <Typography.Text strong style={{ color: "#fff" }}>
              {t("proctoring.activity")}
            </Typography.Text>
            {activity.length === 0 && (
              <Typography.Paragraph style={{ color: "#8c8c8c", marginTop: 8 }}>{t("proctoring.noActivity")}</Typography.Paragraph>
            )}
            {activity.map((a) => (
              <div
                key={a.id}
                onClick={() => a.identity && setFocusedId(a.identity)}
                style={{
                  cursor: a.identity ? "pointer" : "default",
                  borderLeft: `3px solid ${TONE_COLOR[a.tone]}`,
                  padding: "4px 8px",
                  marginTop: 6,
                  color: "#e6e6e6",
                  fontSize: 12,
                }}
              >
                <span style={{ color: "#8c8c8c", marginRight: 6 }}>{dayjs(a.at).format("HH:mm:ss")}</span>
                {a.text}
              </div>
            ))}
          </div>
        )}
      </Flex>

      {/* In fullscreen only the wall element is painted, so the drawer must be mounted inside it. */}
      {session.canManageAssignments && (
        <ProctorAssignmentsDrawer
          examTermId={termId}
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          getContainer={isFullscreen ? () => wallRef.current ?? document.body : undefined}
        />
      )}
    </div>
  );
};
